import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponseHeaders } from "axios";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { CoachReportSummary } from "../../../../../api/services/reports/models";
import {
  useExportCoachReport,
  useGetCoachReports,
} from "../../../../../api/services/reports/reportsApi";
import { MuiBox } from "../../../../../components";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { getFileNameFromHeader } from "../../../helper";
import { CoachReportFormKeys } from "../../../modelsAndSchemas/models";
import { CoachReportFormSchema } from "../../../modelsAndSchemas/ValidationSchema";
import { CoachReportForm } from "../coachReportForm/CoachReportForm";
import { CoachReportResult } from "../coachReportResult/CoachReportResult";

export const CoachReportSearch = () => {
  const {
    commonValidationMessages: { requiredValidation },
    commonLabels: { someThingWentWrongPleaseTryAgainLater },
  } = useSiteCore();
  const methods = useForm<CoachReportFormKeys>({
    defaultValues: {
      coaches: [],
    },
    resolver: yupResolver(CoachReportFormSchema(requiredValidation)),
  });
  const [coachSummaryList, setCoachSummaryList] = useState<
    CoachReportSummary[]
  >([]);

  const {
    mutateAsync: getCoachReports,
    data: coachReportList,
    isPending: isCoachReportLoading,
    isSuccess: isGetCoachReportSuccess,
    isError: isGetCoachReportError,
    isFiltered: isCoachReportFiltered,
    filterParams: coachReportListFilteredParams,
  } = useGetCoachReports();

  const {
    mutateAsync: exportCoachReport,
    isPending: isExportCoachReportLoading,
    isError: isExportCoachReportError,
  } = useExportCoachReport();

  const dispatch = useDispatch();

  const isApiLoading = isCoachReportLoading || isExportCoachReportLoading;
  const isApiError = isGetCoachReportError || isExportCoachReportError;

  useEffect(() => {
    if (isApiLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isApiLoading]);

  useEffect(() => {
    if (isGetCoachReportSuccess) {
      setCoachSummaryList(coachReportList?.data ?? []);
    }
  }, [isGetCoachReportSuccess, coachReportList]);

  useEffect(() => {
    if (isApiError) {
      dispatch(
        toggleToast({
          open: true,
          message: someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isApiError]);

  const { handleSubmit } = methods;
  const handleSearchFormSubmit = (data: CoachReportFormKeys) => {
    getCoachReports({
      coachIds: data.coaches,
    });
  };

  const handleExport = async () => {
    const response = await exportCoachReport(coachReportListFilteredParams);
    const filename = getFileNameFromHeader(
      response.headers as AxiosResponseHeaders,
    );
    saveAs(response.data, filename);
  };

  const onResetSearch = () => {
    setCoachSummaryList([]);
  };

  return (
    <MuiBox>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSearchFormSubmit)}>
          <CoachReportForm onResetSearch={onResetSearch} />
        </form>
      </FormProvider>
      <CoachReportResult
        onExport={handleExport}
        isFilteredCoachReport={isCoachReportFiltered}
        coachReports={coachSummaryList}
      />
    </MuiBox>
  );
};
